import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import {
  LiabilitiesType,
  LienPriorityType,
  LoanType,
  PaymentExclusionReasonType,
  RefinancePurposeType,
  SourceOfFundsType,
  TypeOfCredit,
} from 'constants/loanApplication';
import {
  getConditionalObject,
  replaceFalsyValuesWithInitialValues,
  replaceStringedBooleanValueWithBoolean,
} from 'utils/data';
import {
  checkBooleanPendingChangeVisibility,
  formatDatePendingChange,
  mergePendingChanges,
} from 'utils/pendingChanges';

import { ALL_BORROWERS_PATH, NEW_RECORD_DATA } from '../../../../FinancialInfo.constants';
import { mapFieldsToFalse } from '../SectionContent.utils';

import {
  checkIsFHADetailsVisible,
  checkIsSubjectPropertyMortgage,
  getAccountTypeFlags,
} from './Liabilities.config';

// LEN-205: a chattel home lien linked to the subject property is the first lien a chattel refinance pays off (the API
// defaults it the same way), so the form proposes First while no priority is picked and First is still free.
export const getChattelHomeLienDefaultLienPriority = ({
  isChattelLoan,
  accountType,
  isSubjectProperty,
  lienPriorityType,
  lienPriorityTypeOptions = [],
}) =>
  isChattelLoan &&
  accountType === TypeOfCredit.INSTALLMENT &&
  isSubjectProperty &&
  !lienPriorityType
    ? (lienPriorityTypeOptions.find(({ value }) => value === LienPriorityType.FIRST) ?? null)
    : null;

export const checkShouldForcePaidOffAndNonCashOut = ({
  isRefinanceFlow,
  isSubjectProperty,
  lienPriorityType,
  isSubordinate,
  isExcludePayment,
  refinancePurpose,
}) => {
  if (!isRefinanceFlow || !isSubjectProperty) {
    return false;
  }

  const isFirstMortgageCondition =
    lienPriorityType === LienPriorityType.FIRST && !isSubordinate && !isExcludePayment;

  const isConsolidateLoansCondition =
    refinancePurpose === RefinancePurposeType.CONSOLIDATE_LOANS &&
    [LienPriorityType.SECOND, LienPriorityType.THIRD, LienPriorityType.FOURTH].includes(
      lienPriorityType,
    ) &&
    isSubordinate;

  return isFirstMortgageCondition || isConsolidateLoansCondition;
};

const checkboxFields = [
  'toBePaidOffAtOrBeforeClosing',
  'qualifiedNonCashOut',
  'isSubordinate',
  'excludePayment',
  'heloc',
  'isAffordableLoan',
  'isOriginalLenderRefinance',
];

const defaultFalsyCheckboxFields = mapFieldsToFalse(checkboxFields);

// The server omits a checkbox it holds as null, so it never reaches the reducer below. Seed rawData with null for every
// checkbox up front; a value the server does send overwrites it. This keeps the send-time guard's original-null lookup
// working for omitted boxes, not only for ones the server returns as an explicit null (FH-3792).
const defaultNullCheckboxFields = _.mapValues(defaultFalsyCheckboxFields, () => null);

const formattedKeys = {
  isPaymentExcluded: 'excludePayment',
  toBePaidOff: 'toBePaidOffAtOrBeforeClosing',
  expenseType: 'liabilityExpenseType',
  propertyId: 'property',
  existingLienUpfrontMIP: 'existingLienUpfrontMip',
};

export const normalizeLiabilitiesReceivedData = data =>
  Object.entries(data).reduce(
    (acc, [id, liabilityData]) => {
      const rawLiabilityData = { ...defaultNullCheckboxFields };

      const formattedLiabilityData = Object.entries(liabilityData).reduce(
        (liabilityDataAcc, [key, value]) => {
          const formattedKey = formattedKeys[key] ?? key;

          switch (true) {
            case key === 'liabilityAccountInfo':
              {
                const { accountNumber, accountType, creditorName } = value;

                liabilityDataAcc.accountNumber = accountNumber;
                liabilityDataAcc.accountType = accountType;
                liabilityDataAcc.companyName = creditorName;
                rawLiabilityData.accountNumber = accountNumber;
                rawLiabilityData.accountType = accountType;
                rawLiabilityData.companyName = creditorName;
              }

              break;

            case checkboxFields.includes(formattedKey) && _.isNil(value):
              liabilityDataAcc[formattedKey] = false;
              rawLiabilityData[formattedKey] = null;
              break;

            case key === 'propertyId':
              liabilityDataAcc[formattedKey] = value;
              rawLiabilityData[formattedKey] = value;
              break;

            default:
              liabilityDataAcc[formattedKey] = value;
              rawLiabilityData[formattedKey] = value;
          }

          return liabilityDataAcc;
        },
        {
          ...defaultFalsyCheckboxFields,
        },
      );

      acc.formattedData[id] = {
        ...formattedLiabilityData,
        loanType: formattedLiabilityData.loanType ?? null,
      };
      acc.rawData[id] = rawLiabilityData;

      return acc;
    },
    {
      formattedData: {},
      rawData: {},
    },
  );

export const normalizeLiabilitiesDataToSend = ({
  deletedRecords = new Set(),
  rawData = {},
  dataToNormalize,
  isRefinanceFlow,
  isChattelLoan = false,
}) => {
  const { normalizedDataToUpdate, normalizedDataToCreate } = Object.entries(dataToNormalize).reduce(
    (acc, [recordId, recordData]) => {
      const {
        accountType,
        dateOpened,
        loanType,
        type,
        liabilityExpenseType,
        property,
        owners,
        heloc,
        qualifiedNonCashOut,
        excludePayment,
        paymentExclusionReason,
        paymentExclusionReasonOtherDescription,
        isSubordinate,
        monthlyPayment,
        monthsRemaining,
        highLimit,
        toBePaidOffAtOrBeforeClosing,
        sourceOfFunds,
        otherFundDescription,
        lienPriorityType,
        monthsDeferred,
        isAffordableLoan,
        isOriginalLenderRefinance,
        existingCaseNumber,
        existingLienUpfrontMip,
        existingLienInterestAmount,
        hudAuthorizationCode,
        closingCosts,
        servicingContactName,
        servicingContactPhoneNumber,
        originalPropertyValue,
        loanMipFactor,
        endorsementDate,
        originalEndorsementDate,
        unearnedMipFactor,
        unearnedMip,
        earnedMip,
        ...restRecordData
      } = recordData;

      if (deletedRecords.has(recordId)) {
        return acc;
      }

      const isOtherLiabilityType = type?.value === LiabilitiesType.OTHER;
      const isOtherSourceOfFunds = sourceOfFunds?.value === SourceOfFundsType.OTHER;
      const isOtherExclusionReason =
        paymentExclusionReason?.value === PaymentExclusionReasonType.OTHER;

      const {
        withLoanType,
        withRemainingMonths,
        withProperty,
        withHELOC,
        withHighLimit,
        withMortgageDetails,
        withChattelHomeLien,
      } = getAccountTypeFlags(accountType?.value, { isChattelLoan });

      const withFHADetails = checkIsFHADetailsVisible({
        isRefinanceFlow,
        accountType: accountType?.value,
        loanType: loanType?.value,
      });

      const isSubjectProperty = property?.isSubjectProperty;
      const withSubjectPropertyMortgageDetails = checkIsSubjectPropertyMortgage({
        withMortgageDetails,
        isSubjectProperty,
      });

      const ownersToSend = owners ? owners.map(({ value }) => value) : rawData[recordId]?.owners;

      const normalizedRecordData = {
        owners: ownersToSend,
        dateOpened: dateOpened ? dayjs(dateOpened).format(COMMON_REQUEST_DATE_FORMAT) : null,
        type: type?.value,
        monthlyPayment,

        ...(isOtherLiabilityType
          ? {
              liabilityExpenseType: liabilityExpenseType?.value,
              ...replaceFalsyValuesWithInitialValues({ isSubordinate }, rawData[recordId]),
            }
          : {
              accountType: accountType?.value,

              ...getConditionalObject(withLoanType, {
                loanType: loanType?.value,
              }),
              ...getConditionalObject(withFHADetails, () => ({
                existingCaseNumber,
                existingLienUpfrontMIP: Number(existingLienUpfrontMip),
                existingLienInterestAmount: Number(existingLienInterestAmount),
                hudAuthorizationCode: hudAuthorizationCode?.trim(),
              })),
              ...getConditionalObject(withHELOC, () => ({
                ...replaceFalsyValuesWithInitialValues({ heloc }, rawData[recordId]),
              })),
              ...getConditionalObject(withRemainingMonths, {
                monthsRemaining,
              }),
              ...getConditionalObject(withProperty, {
                propertyId: property?.value ?? null,
              }),
              ...getConditionalObject(withChattelHomeLien && !!property?.value, {
                lienPriorityType: lienPriorityType?.value,
              }),
              ...getConditionalObject(withHighLimit || heloc, {
                highLimit,
              }),
              ...getConditionalObject(toBePaidOffAtOrBeforeClosing, () => ({
                ...replaceFalsyValuesWithInitialValues({ qualifiedNonCashOut }, rawData[recordId]),
              })),
              ...getConditionalObject(withMortgageDetails, () => ({
                sourceOfFunds: sourceOfFunds?.value,
                ...getConditionalObject(isOtherSourceOfFunds, {
                  otherFundDescription,
                }),
                lienPriorityType: lienPriorityType?.value,
                monthsDeferred,
                ...replaceFalsyValuesWithInitialValues(
                  { isAffordableLoan, isOriginalLenderRefinance },
                  rawData[recordId],
                ),
              })),
              ...getConditionalObject(withSubjectPropertyMortgageDetails, () => ({
                closingCosts: closingCosts ? Number(closingCosts) : null,
                servicingContactName: servicingContactName?.trim() || null,
                servicingContactPhoneNumber: servicingContactPhoneNumber?.trim() || null,
                originalPropertyValue: originalPropertyValue ? Number(originalPropertyValue) : null,
                loanMipFactor: loanMipFactor ? Number(loanMipFactor) : null,
                endorsementDate: endorsementDate
                  ? dayjs(endorsementDate).format(COMMON_REQUEST_DATE_FORMAT)
                  : null,
                originalEndorsementDate: originalEndorsementDate
                  ? dayjs(originalEndorsementDate).format(COMMON_REQUEST_DATE_FORMAT)
                  : null,
              })),
              ...replaceFalsyValuesWithInitialValues(
                { toBePaidOffAtOrBeforeClosing, isSubordinate, excludePayment },
                rawData[recordId],
              ),
              ...getConditionalObject(excludePayment, () => ({
                paymentExclusionReason: paymentExclusionReason?.value,
                ...getConditionalObject(isOtherExclusionReason, {
                  paymentExclusionReasonOtherDescription,
                }),
              })),
              ...restRecordData,
            }),
      };

      if (recordId.startsWith(NEW_RECORD_DATA.id)) {
        acc.normalizedDataToCreate.push(normalizedRecordData);
      }

      if (!recordId.startsWith(NEW_RECORD_DATA.id)) {
        acc.normalizedDataToUpdate[recordId] = normalizedRecordData;
      }

      return acc;
    },
    { normalizedDataToUpdate: {}, normalizedDataToCreate: [] },
  );

  return {
    create: normalizedDataToCreate,
    update: normalizedDataToUpdate,
    delete: Array.from(deletedRecords),
  };
};

export const normalizeForm = ({
  form,
  recordId,
  liabilityType,
  accountType,
  initialFormValuesRef,
  borrowerId,
  ownersOptions,
  lienPriorityTypeOptions,
  setValidationErrors,
  isRefinanceFlow,
  isChattelLoan = false,
}) => {
  const {
    dateOpened,
    liabilityExpenseType,
    monthlyPayment,
    type,
    heloc,
    loanType,
    accountType: accountTypeForm,
    monthsRemaining,
    property,
    highLimit,
    qualifiedNonCashOut,
    isSubordinate,
    toBePaidOffAtOrBeforeClosing,
    owners,
    lienPriorityType,
    sourceOfFunds,
    otherFundDescription,
    monthsDeferred,
    isAffordableLoan,
    isOriginalLenderRefinance,
    existingCaseNumber,
    existingLienUpfrontMip,
    existingLienInterestAmount,
    hudAuthorizationCode,
    paymentExclusionReason,
    paymentExclusionReasonOtherDescription,
    closingCosts,
    servicingContactName,
    servicingContactPhoneNumber,
    originalPropertyValue,
    loanMipFactor,
    endorsementDate,
    originalEndorsementDate,
    unearnedMipFactor,
    unearnedMip,
    earnedMip,
    ...restRecordData
  } = form[recordId] ?? {};

  const isOtherLiabilityType = liabilityType === LiabilitiesType.OTHER;
  const isLiabilityType = liabilityType === LiabilitiesType.LIABILITY;
  const isOtherSourceOfFunds = sourceOfFunds === SourceOfFundsType.OTHER;

  const {
    withLoanType,
    withRemainingMonths,
    withProperty,
    withHELOC,
    withHighLimit,
    withMortgageDetails,
    withChattelHomeLien,
  } = getAccountTypeFlags(accountType, { isChattelLoan });

  const withFHADetails = checkIsFHADetailsVisible({
    isRefinanceFlow,
    accountType,
    loanType: loanType?.value,
  });

  const isSubjectProperty = property?.isSubjectProperty;
  const withSubjectPropertyMortgageDetails = checkIsSubjectPropertyMortgage({
    withMortgageDetails,
    isSubjectProperty,
  });

  const initialOwners =
    recordId.startsWith(NEW_RECORD_DATA.id) &&
    ownersOptions.length &&
    borrowerId !== ALL_BORROWERS_PATH
      ? [ownersOptions.find(({ value }) => value === borrowerId)]
      : owners;

  const optionNotAvailable =
    lienPriorityType &&
    !_.some(lienPriorityTypeOptions, option => option.value === lienPriorityType?.value);

  setValidationErrors(prev => {
    const currentError = prev[recordId]?.lienPriorityType;
    const newError = optionNotAvailable ? '' : currentError;

    if (currentError === newError) {
      return prev;
    }

    return {
      ...prev,
      [recordId]: {
        ...prev[recordId],
        lienPriorityType: newError,
      },
    };
  });

  return {
    ...form,
    [recordId]: {
      dateOpened: dateOpened ?? initialFormValuesRef.current[recordId]?.dateOpened,
      monthlyPayment,
      type,
      owners: owners ?? initialOwners,
      isSubordinate,
      ...getConditionalObject(isOtherLiabilityType, { liabilityExpenseType }),
      ...getConditionalObject(isLiabilityType, () => ({
        accountType: accountTypeForm,
        ...getConditionalObject(withMortgageDetails, () => ({
          lienPriorityType: optionNotAvailable ? null : lienPriorityType,
          sourceOfFunds,
          ...getConditionalObject(isOtherSourceOfFunds, { otherFundDescription }),
          monthsDeferred,
          isAffordableLoan,
          isOriginalLenderRefinance,
        })),
        ...getConditionalObject(withChattelHomeLien && isSubjectProperty, () => ({
          lienPriorityType: optionNotAvailable ? null : lienPriorityType,
        })),
        ...getConditionalObject(withLoanType, {
          loanType,
        }),
        ...getConditionalObject(withFHADetails, {
          existingCaseNumber,
          existingLienUpfrontMip,
          existingLienInterestAmount,
          hudAuthorizationCode,
        }),
        ...getConditionalObject(withSubjectPropertyMortgageDetails, {
          closingCosts,
          servicingContactName,
          servicingContactPhoneNumber,
          originalPropertyValue,
          loanMipFactor,
          endorsementDate,
          originalEndorsementDate,
          unearnedMipFactor,
          unearnedMip,
          earnedMip,
        }),
        ...getConditionalObject(withHELOC, {
          heloc,
        }),
        ...getConditionalObject(withRemainingMonths, {
          monthsRemaining,
        }),
        ...getConditionalObject(withProperty, {
          property,
        }),
        ...getConditionalObject(withHighLimit || heloc, {
          highLimit,
        }),
        ...getConditionalObject(toBePaidOffAtOrBeforeClosing, {
          qualifiedNonCashOut,
        }),
        toBePaidOffAtOrBeforeClosing,
        ...getConditionalObject(restRecordData.excludePayment, () => ({
          paymentExclusionReason,
          ...getConditionalObject(
            paymentExclusionReason?.value === PaymentExclusionReasonType.OTHER,
            { paymentExclusionReasonOtherDescription },
          ),
        })),
        ...restRecordData,
      })),
    },
  };
};

const filterPendingChanges = (pendingChanges, liabilities) =>
  Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const filteredPendingChanges = Object.entries(recordPendingChanges).reduce(
      (filteredPendingChangesAcc, [pendingChangeKey, pendingChangeValue]) => {
        const liabilityTypePendingChangeNewValue = recordPendingChanges.type?.newValue;
        const accountTypePendingChangeNewValue =
          recordPendingChanges.accountType?.newValue ??
          liabilities?.find(liability => liability.id === recordId)?.liabilityAccountType;
        const loanTypePendingChangeNewValue = recordPendingChanges.loanType?.newValue;
        const paidOffPendingChangeNewValue =
          recordPendingChanges.toBePaidOffAtOrBeforeClosing?.newValue;
        const helocPendingChangeNewValue = recordPendingChanges.heloc?.newValue;
        const sourceOfFundsChangeNewValue = recordPendingChanges.sourceOfFunds?.newValue;
        const excludePaymentPendingChangeNewValue = recordPendingChanges.excludePayment?.newValue;
        const exclusionReasonPendingChangeNewValue =
          recordPendingChanges.paymentExclusionReason?.newValue;

        const isLiabilityTypeChanged = liabilityTypePendingChangeNewValue !== undefined;
        const isAccountTypeChanged = accountTypePendingChangeNewValue !== undefined;
        const isLoanTypeChanged = loanTypePendingChangeNewValue !== undefined;
        const isHelocChanged = helocPendingChangeNewValue !== undefined;
        const isSourceOfFundsChanged = sourceOfFundsChangeNewValue !== undefined;
        const isPaidOffChanged = paidOffPendingChangeNewValue !== undefined;
        const isExcludePaymentChanged = excludePaymentPendingChangeNewValue !== undefined;
        const isExclusionReasonChanged = exclusionReasonPendingChangeNewValue !== undefined;

        const {
          withHELOC,
          withRemainingMonths,
          withLoanType,
          withProperty,
          withHighLimit,
          withMortgageDetails,
        } = getAccountTypeFlags(accountTypePendingChangeNewValue);

        const getCondition = () => {
          switch (pendingChangeKey) {
            case 'numberRemainingPayments':
            case 'interestRate':
            case 'lastMonth':
              return false;

            case 'companyName':
            case 'accountType':
            case 'accountNumber':
            case 'unpaidBalance':
              return (
                !isLiabilityTypeChanged ||
                liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY
              );

            case 'liabilityExpenseType':
              return (
                !isLiabilityTypeChanged ||
                liabilityTypePendingChangeNewValue === LiabilitiesType.OTHER
              );

            case 'loanType':
            case 'monthsRemaining':
            case 'property':
            case 'heloc':
            case 'sourceOfFunds':
            case 'lienPriorityType':
            case 'isAffordableLoan':

            case 'isOriginalLenderRefinance': {
              const conditionIfAccountTypeChanged = {
                loanType: withLoanType,
                monthsRemaining: withRemainingMonths,
                property: withProperty,
                heloc: withHELOC && checkBooleanPendingChangeVisibility(pendingChangeValue),
                sourceOfFunds: withMortgageDetails,
                lienPriorityType: withMortgageDetails,
                isAffordableLoan:
                  withMortgageDetails && checkBooleanPendingChangeVisibility(pendingChangeValue),
                isOriginalLenderRefinance:
                  withMortgageDetails && checkBooleanPendingChangeVisibility(pendingChangeValue),
              }[pendingChangeKey];

              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                (!isAccountTypeChanged || conditionIfAccountTypeChanged)
              );
            }

            case 'monthsDeferred':
              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                (!isAccountTypeChanged || withMortgageDetails) &&
                (Number(pendingChangeValue?.newValue) || 0) !==
                  (Number(pendingChangeValue?.initialValue) || 0)
              );

            case 'existingCaseNumber':
            case 'existingLienUpfrontMip':
            case 'existingLienInterestAmount':
            case 'hudAuthorizationCode':
              return (
                ((!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                  (!isAccountTypeChanged ||
                    accountTypePendingChangeNewValue === TypeOfCredit.MORTGAGE) &&
                  !isLoanTypeChanged) ||
                loanTypePendingChangeNewValue === LoanType.FHA
              );

            case 'highLimit':
              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                (!isAccountTypeChanged ||
                  withHighLimit ||
                  (withHELOC && (!isHelocChanged || helocPendingChangeNewValue)))
              );

            case 'excludePayment':
            case 'toBePaidOffAtOrBeforeClosing':
              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'qualifiedNonCashOut':
              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                (!isPaidOffChanged ||
                  replaceStringedBooleanValueWithBoolean(paidOffPendingChangeNewValue)) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'otherFundDescription':
              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                (!isAccountTypeChanged ||
                  (withMortgageDetails &&
                    (!isSourceOfFundsChanged ||
                      sourceOfFundsChangeNewValue === SourceOfFundsType.OTHER)))
              );

            case 'paymentExclusionReason':
            case 'paymentExclusionReasonOtherDescription':
              return (
                (!isLiabilityTypeChanged ||
                  liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                (!isExcludePaymentChanged ||
                  replaceStringedBooleanValueWithBoolean(excludePaymentPendingChangeNewValue)) &&
                (pendingChangeKey !== 'paymentExclusionReasonOtherDescription' ||
                  !isExclusionReasonChanged ||
                  exclusionReasonPendingChangeNewValue === PaymentExclusionReasonType.OTHER)
              );

            default:
              if (
                [
                  'closingCosts',
                  'servicingContactName',
                  'servicingContactPhoneNumber',
                  'originalPropertyValue',
                  'loanMipFactor',
                  'endorsementDate',
                  'originalEndorsementDate',
                  'unearnedMipFactor',
                  'unearnedMip',
                  'earnedMip',
                ].includes(pendingChangeKey)
              ) {
                return (
                  (!isLiabilityTypeChanged ||
                    liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY) &&
                  (!isAccountTypeChanged || withMortgageDetails)
                );
              }

              return true;
          }
        };

        const condition = getCondition();

        if (condition) {
          filteredPendingChangesAcc[pendingChangeKey] = pendingChangeValue;
        }

        return filteredPendingChangesAcc;
      },
      {},
    );

    acc[recordId] = filteredPendingChanges;

    return acc;
  }, {});

const formatPendingChangesToForm = pendingChanges => {
  return Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const { dateOpened, endorsementDate, originalEndorsementDate, ...restRecordPendingChanges } =
      recordPendingChanges;

    acc[recordId] = {
      ...restRecordPendingChanges,
      ...getConditionalObject(dateOpened, () => ({
        dateOpened: formatDatePendingChange(dateOpened),
      })),
      ...getConditionalObject(endorsementDate, () => ({
        endorsementDate: formatDatePendingChange(endorsementDate),
      })),
      ...getConditionalObject(originalEndorsementDate, () => ({
        originalEndorsementDate: formatDatePendingChange(originalEndorsementDate),
      })),
    };

    return acc;
  }, {});
};

export const normalizeLiabilitiesPendingChanges = ({
  initialPendingChanges,
  formPendingChanges = {},
  isEditFlow = false,
  liabilities,
}) => {
  const mergedPendingChanges = mergePendingChanges(initialPendingChanges, formPendingChanges);

  const filteredPendingChanges = filterPendingChanges(mergedPendingChanges, liabilities);

  return isEditFlow ? formatPendingChangesToForm(filteredPendingChanges) : filteredPendingChanges;
};
